import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, TextField, SelectField, MenuItem, Table, TableRow, TableHeader, TableHeaderColumn, Toggle } from "material-ui"
import strings from "../../localization/strings"
import { IPunchRule, EPunchRuleType, EPunchRuleVersion } from "../../models"
import * as utils from "../../helpers/utils"
import { grey200, grey300, red500 } from "material-ui/styles/colors"
import ScriptCodeEditor from "../../components/common/script_code_editor/ScriptCodeEditor"
import * as api from "../../api"
import ChooseCSVFile from "../../components/common/ChooseCSVFile"
import AccountStore from "../../stores/AccountStore"

interface IProps {
    existingPunchRules: IPunchRule[]
    editPunchRule: IPunchRule
    handleClose(): void
    handleAdd(punchRule: IPunchRule): void
    handleUpdate(punchRule: IPunchRule): void
}
interface IState {
    name: string
    errorName: string
    description: string
    scriptCode: string
    errorScriptCode: string
    errorCSVFile: string
    uploadFileData: any
    selectedPunchRuleType: EPunchRuleType
    showChooseCSVFile: boolean
    stage: number,
    autoApprovePunchsets : boolean
}

export default class AddPunchRuleDialog extends React.Component<IProps, IState> {
    private singleOrMultiDayHeaderCols = ["Rule Name", "Hierarchy Name", "Hierarchy Cost Code", "Position List", "Day Range", "Target PayCode", "Pay Match Policy", "Punch Util Version", "Do Not Apply To Exempt", "Field1 Match List", "Field2 Match List", "Field3 Match List", "Field4 Match List", "Pay Type Exclusion List", "Job Code Inclusion List"]
    private singleOrMultiDayRequiredHeaderCols = ["Position List", "Day Range", "Target PayCode", "Pay Match Policy", "Punch Util Version", "Do Not Apply To Exempt", "Field1 Match List", "Field2 Match List", "Field3 Match List", "Field4 Match List"]
    private guaranteedMinimumHeaderCols = ["Rule Name", "DayRange", "MatchPosition List", "Guaranteed Minimum Hours", "Target PayTypeCode", "Minimum By PunchSet", "Do Not Apply To Exempt", "Field1 Match List", "Field2 Match List", "Field3 Match List", "Field4 Match List"]
    private guaranteedMinimumRequiredHeaderCols = ["Rule Name", "DayRange", "Guaranteed Minimum Hours", "Target PayTypeCode"]
    private mealPenaltyWaiverHeaderCols = ["Rule Name", "DayRange", "Min PunchSets Duration in Minutes", "Break Duration in Minute", "Bonus Duration In Minute", "Target PayTypeCode", "Do Not Apply To Exempt", "User Match Field"]
    private mealPenaltyWaiverRequiredHeaderCols = ["Rule Name", "DayRange", "Min PunchSets Duration in Minutes", "Break Duration in Minute", "Bonus Duration In Minute", "Target PayTypeCode"]
    private bonusPayHeaderCols = ["Rule Name", "DayRange", "Minimum PunchSets Hour", "PayMatch Policy", "MatchPosition List", "Bonus Duration In Minute", "Target PayTypeCode", "Do Not Apply To Exempt", "Field1 Match List", "Field2 Match List", "Field3 Match List", "Field4 Match List"]
    private bonusPayRequiredHeaderCols = ["Rule Name", "DayRange", "Minimum PunchSets Hour", "Bonus Duration In Minute", "Target PayTypeCode"]
    constructor(props: IProps) {
        super(props)
        this.state = {
            name: props.editPunchRule ? props.editPunchRule.Name : "",
            errorName: "",
            description: props.editPunchRule ? props.editPunchRule.Description : "",
            scriptCode: props.editPunchRule ? props.editPunchRule.CSharpScript : "",
            errorScriptCode: "",
            errorCSVFile: "",
            uploadFileData: null,
            selectedPunchRuleType: props.editPunchRule ? props.editPunchRule.RuleType : EPunchRuleType.SingleDay,
            showChooseCSVFile: true,
            stage: props.editPunchRule ? props.editPunchRule.Stage : 0,
            autoApprovePunchsets : props.editPunchRule ? props.editPunchRule.AutoApprovePunchSets : true,
        }
        autobind(this)
    }

    handleSave() {
        if (!this.checkValidate()) return
        const payload: IPunchRule = {
            Id: this.props.editPunchRule ? this.props.editPunchRule.Id : utils.guid(),
            Name: this.state.name.trim(),
            Description: this.state.description.trim(),
            CSharpScript: this.state.scriptCode.trim(),
            DailyRule: this.state.selectedPunchRuleType === EPunchRuleType.DailyPunchRule,
            FullTimecard: [EPunchRuleType.GuaranteedMinimumHourRule, EPunchRuleType.BonusPayRule, EPunchRuleType.MealPenaltyRule].includes(this.state.selectedPunchRuleType),
            RuleType: this.state.selectedPunchRuleType,
            RuleVersion: this.props.editPunchRule ? this.props.editPunchRule.RuleVersion : EPunchRuleVersion.V2,
            Stage: this.state.stage,
            AutoApprovePunchSets : this.state.autoApprovePunchsets
        }
        this.props.editPunchRule ?
            this.props.handleUpdate(payload) :
            this.props.handleAdd(payload)
    }

    checkValidate() {
        let isValidate = true
        if (!this.state.name.trim()) {
            this.setState({ errorName: strings.NameRequired })
            isValidate = false
        }
        else {
            if (this.props.existingPunchRules
                .filter(item => this.props.editPunchRule ? this.props.editPunchRule.Id !== item.Id : true)
                .find(item => item.Name === this.state.name.trim())
            ) {
                this.setState({ errorName: strings.Name_is_duplicated })
                isValidate = false
            }
            else {
                this.setState({ errorName: "" })
            }
        }

        if (!this.state.scriptCode.trim()) {
            this.setState({ errorScriptCode: strings.This_field_is_required })
            isValidate = false
        }
        else {
            this.setState({ errorScriptCode: "" })
        }

        return isValidate
    }

    async getScriptCode() {
        let compileRes
        switch (this.state.selectedPunchRuleType) {
            case EPunchRuleType.SingleDay:
                try {
                    compileRes = await api.singleDaySDRule(this.state.uploadFileData)
                } catch (error) {
                    this.setState({ errorCSVFile: strings.Incorrect_CSV_File })
                }
                break
            case EPunchRuleType.MultiDay:
                try {
                    compileRes = await api.multipleDaySDRule(this.state.uploadFileData)
                } catch (error) {
                    this.setState({ errorCSVFile: strings.Incorrect_CSV_File })
                }
                break
            case EPunchRuleType.PunchDayGroupingRule:
                compileRes = await api.punchDayGroupingRule()
                break
            case EPunchRuleType.DailyPunchRule:
                compileRes = await api.dailyPunchRule()
                break
            case EPunchRuleType.GuaranteedMinimumHourRule:
                try {
                    compileRes = await api.GuaranteedMinimumHourPunchRule(this.state.uploadFileData)
                } catch (error) {
                    this.setState({ errorCSVFile: strings.Incorrect_CSV_File })
                }
                break
            case EPunchRuleType.MealPenaltyRule:
                try {
                    compileRes = await api.MealPenaltyWaiverPunchRule(this.state.uploadFileData)
                } catch (error) {
                    this.setState({ errorCSVFile: strings.Incorrect_CSV_File })
                }
                break
            case EPunchRuleType.BonusPayRule:
                try {
                    compileRes = await api.BonusPayPunchRule(this.state.uploadFileData)
                } catch (error) {
                    this.setState({ errorCSVFile: strings.Incorrect_CSV_File })
                }
                break
        }
        this.setState({ scriptCode: compileRes.data })
    }

    getImportHeaderCols() {
        switch (this.state.selectedPunchRuleType) {
            case EPunchRuleType.SingleDay:
                return this.singleOrMultiDayHeaderCols
            case EPunchRuleType.MultiDay:
                return this.singleOrMultiDayHeaderCols
            case EPunchRuleType.GuaranteedMinimumHourRule:
                return this.guaranteedMinimumHeaderCols
            case EPunchRuleType.MealPenaltyRule:
                return this.mealPenaltyWaiverHeaderCols
            case EPunchRuleType.BonusPayRule:
                return this.bonusPayHeaderCols
        }
    }

    getRequiredHeaderCols() {
        switch (this.state.selectedPunchRuleType) {
            case EPunchRuleType.SingleDay:
                return this.singleOrMultiDayRequiredHeaderCols
            case EPunchRuleType.MultiDay:
                return this.singleOrMultiDayRequiredHeaderCols
            case EPunchRuleType.GuaranteedMinimumHourRule:
                return this.guaranteedMinimumRequiredHeaderCols
            case EPunchRuleType.MealPenaltyRule:
                return this.mealPenaltyWaiverRequiredHeaderCols
            case EPunchRuleType.BonusPayRule:
                return this.bonusPayRequiredHeaderCols
        }
    }

    render() {
        return (
            <Dialog
                open
                title={strings.Punch_Rule}
                titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
                contentStyle={{ width: "95%", maxWidth: "none" }}
                autoScrollBodyContent
                actions={[
                    <FlatButton
                        data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Btn-CloseAddPunchRuleDialog"}
                        label={strings.CANCEL}
                        secondary
                        onClick={this.props.handleClose}
                    />,
                    <FlatButton
                        data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Btn-SavePunchRule"}
                        label={strings.SAVE}
                        primary
                        onClick={this.handleSave}
                    />
                ]}
            >
                <div style={{ paddingRight: 20, paddingLeft: 20 }}>
                    <div style={{ display: "flex" }}>
                        <TextField
                            data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Add-Entry-Name"}
                            fullWidth
                            style={{ marginRight: 50 }}
                            floatingLabelText={strings.Name}
                            value={this.state.name}
                            onChange={(e, value) => { this.setState({ name: value }, () => this.checkValidate()) }}
                            errorText={this.state.errorName}
                            errorStyle={{ position: "absolute", bottom: -10 }}
                        />
                        <TextField
                            data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Add-Entry-Description"}
                            fullWidth
                            floatingLabelText={strings.Description}
                            value={this.state.description}
                            onChange={(e, value) => { this.setState({ description: value }) }}
                        />
                    </div>
                    <div style={{ display: "flex", marginTop: 20 }}>
                        {AccountStore.getPunchRuleStageEnabled() &&
                            <SelectField
                                data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Add-Select-PunchRuleStage"}
                                style={{ width: 75, marginRight: 50 }}
                                floatingLabelText={strings.Stage}
                                value={this.state.stage}
                                onChange={(event, index, value) => this.setState({ stage: value })}
                            >
                                <MenuItem value={0} primaryText="0" />
                                <MenuItem value={1} primaryText="1" />
                            </SelectField>
                        }
                        <SelectField
                            data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Add-Select-PunchRuleType"}
                            style={{ width: 225 }}
                            floatingLabelText={strings.Punch_Rule_Type}
                            value={this.state.selectedPunchRuleType}
                            onChange={(event, index, value) =>
                                this.setState({ errorCSVFile: "", showChooseCSVFile: false, selectedPunchRuleType: value, scriptCode: "", uploadFileData: null, name: "" }, () =>
                                    this.setState({ showChooseCSVFile: true }, () =>
                                        [EPunchRuleType.PunchDayGroupingRule, EPunchRuleType.DailyPunchRule].includes(value) &&
                                        this.getScriptCode()
                                    )
                                )
                            }
                        >
                            <MenuItem value={EPunchRuleType.SingleDay} primaryText={strings.Single_Day} />
                            <MenuItem value={EPunchRuleType.MultiDay} primaryText={strings.Multi_Day} />
                            <MenuItem value={EPunchRuleType.PunchDayGroupingRule} primaryText={strings.Punch_Day_Grouping} />
                            <MenuItem value={EPunchRuleType.DailyPunchRule} primaryText={strings.Daily_Punch_Rule} />
                            <MenuItem value={EPunchRuleType.GuaranteedMinimumHourRule} primaryText={strings.Guaranteed_Minimum} />
                            <MenuItem value={EPunchRuleType.MealPenaltyRule} primaryText={strings.Meal_Penalty_Waiver} />
                            <MenuItem value={EPunchRuleType.BonusPayRule} primaryText={strings.Bonus_Pay} />
                        </SelectField>

                        {this.state.showChooseCSVFile && (this.state.selectedPunchRuleType !== EPunchRuleType.PunchDayGroupingRule && this.state.selectedPunchRuleType !== EPunchRuleType.DailyPunchRule) &&
                            <div data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Select-CSVFile"} style={{ marginTop: -10, marginLeft: 20 }}>
                                <ChooseCSVFile
                                    errorCSVFile={this.state.errorCSVFile}
                                    handleChooseFile={(fileName, uploadFileData) =>
                                        this.setState({ errorCSVFile: "", uploadFileData, name: fileName.split(".")[0].trim() }, () =>
                                            this.getScriptCode()
                                        )
                                    }
                                />
                                <span style={{ marginLeft: 10, color: red500, fontSize: 12 }}>{this.state.errorCSVFile}</span>
                            </div>
                        }
                         <Toggle
                            data-intercom-target={"menu-Hierarchy-Settings-PunchRules-Toggle-Auto-Approve-Punchsets"}
                            label={strings.Auto_Approve_Punchsets}
                            style={{ width: 240, marginTop: 5, marginLeft: 50 }}
                            toggled={this.state.autoApprovePunchsets}
                            onToggle={(e, value) => this.setState({ autoApprovePunchsets: value })}
                        />
                    </div>
                    
                    {(this.state.selectedPunchRuleType !== EPunchRuleType.PunchDayGroupingRule && this.state.selectedPunchRuleType !== EPunchRuleType.DailyPunchRule) &&
                        <div style={{ padding: 10, backgroundColor: grey200 }}>
                            <div style={{ textAlign: "center" }}>{strings.Expected_CSV_File_Format}</div>
                            <Table selectable={false} wrapperStyle={{ marginTop: 5 }}>
                                <TableHeader
                                    displaySelectAll={false}
                                    adjustForCheckbox={false}
                                    enableSelectAll={false}
                                >
                                    <TableRow style={{ height: 30 }}>
                                        {this.getImportHeaderCols().map(col =>
                                            <TableHeaderColumn key={col} style={{ height: 30, width: 200, paddingLeft: 10, paddingRight: 10, borderRightStyle: "solid", borderRightColor: grey300, borderRightWidth: 1, }}>
                                                {col}
                                            </TableHeaderColumn>
                                        )}
                                    </TableRow>
                                    <TableRow style={{ height: 25 }}>
                                        {this.getImportHeaderCols().map(col =>
                                            <TableHeaderColumn key={col} style={{ height: 25, width: 200, paddingLeft: 10, paddingRight: 10, borderRightStyle: "solid", borderRightColor: grey300, borderRightWidth: 1, }} />
                                        )}
                                    </TableRow>
                                </TableHeader>
                            </Table>
                            <div style={{ marginTop: 10, fontSize: 12 }}>
                                <span style={{ marginRight: 10 }}>{strings.Required_Columns}:</span>
                                {this.getRequiredHeaderCols().map((col, index) =>
                                    <span key={index} style={{ marginRight: 10 }}>
                                        {col}{index !== this.getRequiredHeaderCols().length - 1 && ","}
                                    </span>
                                )}
                            </div>
                        </div>
                    }
                    <ScriptCodeEditor
                        scriptCode={this.state.scriptCode}
                        errorScriptCode={this.state.errorScriptCode}
                        handleChange={scriptCode => this.setState({ scriptCode }, () => this.checkValidate())}
                    />
                </div>
            </Dialog >
        )
    }
}